import {
  type EventCatalogEntry,
  type EventCatalogTier,
  formatMoneyMinor,
  lookup,
} from "@repo/vihangam-yog-shared";

/** The one field of a tier availability row the Kund tally reads. */
interface TierBooked {
  tierId: string;
  booked: number;
}

interface KundCount {
  taken: number;
  total: number;
  remaining: number;
}

/**
 * How many Kunds are spoken for: each tier's bookings weighted by the
 * Kunds one booking of that tier occupies (a tier without a weight counts
 * as one). Null when the event isn't Kund-based, so callers hide the line.
 */
export function kundCount(
  event: EventCatalogEntry,
  availability: readonly TierBooked[],
): KundCount | null {
  const total = event.price?.kundsTotal;
  if (total === undefined) {
    return null;
  }
  const weights = new Map(
    (event.price?.tiers ?? []).map((t) => [t.id, t.kunds ?? 1]),
  );
  const raw = availability.reduce(
    (sum, a) => sum + a.booked * (weights.get(a.tierId) ?? 1),
    0,
  );
  const taken = Math.min(total, Math.round(raw));
  return { taken, total, remaining: total - taken };
}

/** The one-liner under the Hawan day: only what is left, as the
 *  organisers asked ("489 Kunds left"), and a plain full-house line at zero. */
export function kundLine({ total, remaining }: KundCount): string {
  if (remaining <= 0) {
    return `All ${total} Kunds are taken`;
  }
  return `${remaining} ${remaining === 1 ? "Kund" : "Kunds"} left`;
}

/** Under this age a person is free on a per-person tier (the backend's
 *  rule, mirrored so the quote on the form matches the charge). */
const PAYER_MIN_AGE = 16;

/** One party member as the quote needs it: the typed age, or null when
 *  it isn't a number yet. An unknown age is charged, as on the server. */
export interface QuoteMember {
  age: number | null;
}

export interface HawanQuote {
  /** Units of the tier: payers on a per-person tier, Kunds otherwise. */
  quantity: number;
  totalCents: number;
  /** The one-line summary under the option, e.g.
   *  "3 people aged 16+ · S$153. Children under 16 are free." */
  line: string;
}

function people(n: number): string {
  return `${n} ${n === 1 ? "person" : "people"}`;
}

function kunds(n: number): string {
  return `${n} ${n === 1 ? "Kund" : "Kunds"}`;
}

/**
 * What the party would pay for `tier`, mirroring the backend's rules: a
 * per-person tier (`seats: 1`) charges everyone 16 or over, and anyone
 * whose age is not typed yet, while children come free; a group tier
 * seats everyone and takes ceil(people / seats) units. Null when the
 * tier has no price yet.
 */
export function hawanQuote(
  party: readonly QuoteMember[],
  tier: EventCatalogTier,
  currency: string,
): HawanQuote | null {
  if (tier.amountCents <= 0) {
    return null;
  }
  const perPerson = tier.seats <= 1;
  const quantity = perPerson
    ? party.filter((p) => p.age === null || p.age >= PAYER_MIN_AGE).length
    : Math.max(1, Math.ceil(party.length / tier.seats));
  const totalCents = tier.amountCents * quantity;
  const money = formatMoneyMinor(totalCents, currency);
  const line = perPerson
    ? `${people(quantity)} aged 16+ · ${money}. Children under 16 are free.`
    : `${people(party.length)} · ${kunds(quantity)} · ${money}`;
  return { quantity, totalCents, line };
}

/** The short title the option card leads with; the catalog label is
 *  written for the admin roster ("Shared Kund, per person"). */
const TIER_TITLES = {
  shared: "Shared Kund",
  kund: "Your own Kund",
} satisfies Record<string, string>;

/** What one Hawan option card on the form says. */
export interface HawanTierCard {
  title: string;
  /** The pricing basis, or "Sold out" / "Price to be announced". */
  detail: string;
}

/**
 * The organisers' pricing sentence for one tier, with the amount from the
 * catalog: "S$51 per person (shared Kund)" for a per-person tier, "S$201
 * per Kund (per family, not shared)" for a whole Kund. Null when the tier
 * has no price yet.
 */
function tierPriceLine(
  tier: EventCatalogTier,
  currency: string,
): string | null {
  if (tier.amountCents <= 0) {
    return null;
  }
  const price = formatMoneyMinor(tier.amountCents, currency);
  return tier.seats <= 1
    ? `${price} per person (shared Kund)`
    : `${price} per Kund (per family, not shared)`;
}

/** Every priced tier's sentence, in catalog order, for a price summary. */
export function priceLines(event: EventCatalogEntry): string[] {
  const price = event.price;
  if (!price) {
    return [];
  }
  return price.tiers.flatMap((t) => tierPriceLine(t, price.currency) ?? []);
}

/** Title and price detail for one Hawan option on the form. */
export function hawanTierCard(
  tier: EventCatalogTier,
  currency: string,
): HawanTierCard {
  const title = lookup(TIER_TITLES, tier.id) ?? tier.label;
  const detail = tierPriceLine(tier, currency) ?? "Price to be announced";
  return { title, detail };
}
